// Mind2Magic CRM — Enhanced Content Template Engine
// Hormozi-style multiplier: 1 idea → 5 formats, multiple variations
// Sara's travel agency voice (Colombian neutral LATAM)

// ========== FORMAT DEFINITIONS ==========

export const FORMAT_TYPES = [
    { id: 'caption', label: 'Post' },
    { id: 'reel', label: 'Reel' },
    { id: 'story', label: 'Story' },
    { id: 'carousel', label: 'Carrusel' },
    { id: 'whatsapp', label: 'WhatsApp' },
]

// ========== HELPERS ==========

const _pick = arr => arr[Math.floor(Math.random() * arr.length)]

function _hashtags(dest) {
    const tags = ['#ViajesEnFamilia', '#TravelPlanner']
    const d = (dest || '').toLowerCase()
    if (d.includes('disney')) tags.push('#DisneyWorld', '#ViajeDisney', '#MagicKingdom')
    if (d.includes('universal')) tags.push('#UniversalOrlando', '#UniversalStudios')
    if (d.includes('crucero') || d.includes('cruise')) tags.push('#CruceroEnFamilia', '#VidaDeCrucero')
    if (d.includes('orlando')) tags.push('#Orlando')
    tags.push('#Mind2Magic')
    return [...new Set(tags)].slice(0, 8).join(' ')
}

function _cta() {
    return _pick([
        'Link en bio para tu asesoria gratuita.',
        'Escribeme por DM y te cuento todo.',
        'La cotizacion es gratis. Link en bio.',
        'Quieres saber cuanto cuesta? Escribeme.',
        'Tu proximo viaje empieza con un mensaje. Link en bio.',
    ])
}

function _reelAudio() {
    return _pick([
        'Audio sugerido: musica emocional/epica',
        'Audio sugerido: trending sound',
        'Audio sugerido: musica de parques (instrumental)',
    ])
}

// ========== BOOKING TEMPLATES ==========

const BOOKING = {
    caption: (ctx, dest, travelers) => _pick([
        `Otra familia rumbo a ${dest}.

${travelers ? `${travelers} listos para la aventura. ` : ''}Cada viaje que planifico es diferente: hotel, parques, restaurantes, Lightning Lane... todo armado a la medida de la familia.

No es lo mismo ir con ninos de 3 que con adolescentes. No es lo mismo un hotel Value que un Deluxe. Esos detalles marcan la diferencia entre un buen viaje y uno increible.

${_cta()}

${_hashtags(dest)}`,

        `Acabo de cerrar otro viaje a ${dest}.

Cuando una familia me dice "queremos ir", empieza un proceso que puede tomar semanas. Cotizacion personalizada, seleccion de hotel, estrategia de parques, dining reservations, Lightning Lane, y mil detalles mas.

${travelers ? `Este viaje: ${travelers}. ` : ''}Todo planificado para que solo se preocupen por disfrutar.

${_cta()}

${_hashtags(dest)}`,

        `Hay algo especial cuando una familia confirma su viaje.

Hoy: ${dest}. ${travelers ? `${travelers} con ` : 'Con '}todo listo para vivir una experiencia que van a recordar siempre.

Mi trabajo es que lleguen sin preocupaciones. Que sepan donde ir primero, donde comer, que filas evitar, y como aprovechar cada minuto.

Eso es planificar con alguien que conoce cada rincon de esos parques.

${_cta()}

${_hashtags(dest)}`,
    ]),

    reel: (ctx, dest, travelers) => _pick([
        `GUION REEL (15-30 seg)

Hook: "Acabo de confirmar otro viaje a ${dest}"

Cuerpo:
- Mostrar pantalla de reserva (sin datos personales)
- "${travelers ? travelers + ', con ' : 'Con '}todo planificado"
- "Hotel, parques, dining, Lightning Lane... cada detalle"

Cierre: "Quieres que planifique el tuyo? Link en bio"

${_reelAudio()}`,

        `GUION REEL (15-30 seg)

Hook: "Cuando me dicen 'ya pagamos el deposito'"

Cuerpo:
- Reaccion genuina de emocion
- "Otra familia va rumbo a ${dest}"
- "${travelers ? travelers + '. ' : ''}Todo planificado paso a paso"
- Mostrar brevemente checklist de planificacion

Cierre: "Si tu tambien quieres planificar tu viaje, escribeme"

${_reelAudio()}`,
    ]),

    story: (ctx, dest, travelers) => _pick([
        `SECUENCIA DE STORIES (3 stories)

Story 1:
Fondo: Color solido o foto de ${dest}
Texto: "VIAJE CONFIRMADO"

Story 2:
Texto: "${dest}"
${travelers ? `Detalle: "${travelers}"` : ''}
Sticker: Countdown al viaje

Story 3:
Encuesta: "Tu tambien quieres ir a ${dest}?"
Opciones: "Si!" / "Algun dia"
Seguimiento: DMs a quienes voten "Si"`,

        `SECUENCIA DE STORIES (3 stories)

Story 1:
Fondo: Pantalla de notificacion
Texto: "Nueva reserva confirmada"

Story 2:
Fondo: Foto de ${dest}
Texto: "Destino: ${dest}"
${travelers ? `Detalle: "${travelers}"` : ''}

Story 3:
Caja de preguntas: "Que quieren saber sobre ${dest}?"
Seguimiento: Responder en stories al dia siguiente`,
    ]),

    carousel: (ctx, dest, travelers) => `CARRUSEL (5 slides para Canva)

Slide 1 (Portada):
Titulo: "Planificar un viaje a ${dest} no es solo comprar boletos"
Subtitulo: "Es armar una experiencia completa"

Slide 2:
Titulo: "Cotizacion personalizada"
Texto: Cada familia tiene presupuesto, edades y preferencias diferentes. No es copiar y pegar.

Slide 3:
Titulo: "Estrategia de parques"
Texto: Que parque cada dia, por donde empezar, que filas evitar, donde comer. Todo calculado.

Slide 4:
Titulo: "Reservas y documentos"
Texto: Hotel, dining, Lightning Lane, transporte. Todo coordinado para que no tengan que pensar en nada.

Slide 5 (CTA):
Titulo: "Tu viaje puede verse asi"
Texto: "Cotizacion gratis. Escribeme por DM o link en bio."

Colores: fondo oscuro, texto claro, acentos azul/morado`,

    whatsapp: (ctx, dest, travelers) => `Hola! Acabo de confirmar un viaje a ${dest}${travelers ? ` para ${travelers}` : ''}. Todo planificado: hotel, parques, restaurantes, cada detalle.

Si estan pensando en viajar, me escriben y les cuento como funciona. La asesoria es gratis.`,
}

// ========== QUOTE TEMPLATES ==========

const QUOTE = {
    caption: (ctx, dest, travelers) => _pick([
        `${dest} sigue en la mente de muchas familias.

Esta semana estuve armando ${ctx.quoteCount || 'varias'} cotizaciones para este destino. Cada familia es diferente: presupuestos, edades de los ninos, preferencias de hotel, temporada.

Por eso cada plan se arma desde cero. No es copiar y pegar.

Si quieres saber cuanto cuesta tu viaje, el primer paso es contarme que tienes en mente.

${_cta()}

${_hashtags(dest)}`,

        `"Cuanto cuesta ir a ${dest}?"

Es la pregunta que mas recibo. Y la respuesta honesta es: depende.

Depende de cuantos viajan, las edades de los ninos, que hotel, cuantos dias de parques, si quieren dining plan, si es temporada alta o baja.

Por eso no doy precios genericos. Cada cotizacion es a la medida.

Esta semana llevo ${ctx.quoteCount || 'varias'} cotizaciones para ${dest}. La tuya puede ser la siguiente.

${_cta()}

${_hashtags(dest)}`,
    ]),

    reel: (ctx, dest, travelers) => _pick([
        `GUION REEL (15-30 seg)

Hook: "Cuanto cuesta ir a ${dest}? Depende."

Cuerpo:
- "No es lo mismo 2 adultos que una familia de 6"
- "No es lo mismo Value que Deluxe"
- "No es lo mismo marzo que septiembre"
- "Por eso cada cotizacion es diferente"

Cierre: "Cuentame tu caso y te armo un plan real"

${_reelAudio()}`,

        `GUION REEL (15-30 seg)

Hook: "Esta semana llevo ${ctx.quoteCount || 'varias'} cotizaciones para ${dest}"

Cuerpo:
- "Y cada una es completamente diferente"
- Mostrar 2-3 variables que cambian el precio
- "Hotel, parques, temporada, edades... todo importa"

Cierre: "La tuya puede ser la siguiente. Escribeme."

${_reelAudio()}`,
    ]),

    story: (ctx, dest) => `SECUENCIA DE STORIES (3 stories)

Story 1:
Texto: "${ctx.quoteCount || 'Varias'} cotizaciones para ${dest} esta semana"
Fondo: Foto de ${dest}

Story 2:
Encuesta: "Te gustaria saber cuanto cuesta ${dest}?"
Opciones: "Si!" / "Ya fui"

Story 3:
Texto: "Escribeme y te armo tu cotizacion gratis"
Sticker: Link / Swipe up`,

    carousel: (ctx, dest) => `CARRUSEL (5 slides para Canva)

Slide 1 (Portada):
"Cuanto cuesta ir a ${dest}? La respuesta real."

Slide 2:
"Depende de cuantos viajan"
2 adultos vs familia de 6 = presupuesto totalmente diferente

Slide 3:
"Depende del hotel"
Value ($150/noche) vs Deluxe ($600/noche). Y cada uno tiene beneficios diferentes.

Slide 4:
"Depende de la temporada"
Septiembre puede costar la mitad de diciembre. Mismos parques, misma magia.

Slide 5 (CTA):
"La unica forma de saberlo es cotizar"
"Escribeme y te doy un precio real para TU familia"`,

    whatsapp: (ctx, dest) => `Esta semana llevo ${ctx.quoteCount || 'varias'} cotizaciones para ${dest}. Cada familia tiene un presupuesto diferente porque depende del hotel, los dias, y la temporada.

Si quieren saber cuanto les costaria, me escriben y les armo su cotizacion gratis.`,
}

// ========== TRENDING TEMPLATES ==========

const TRENDING = {
    caption: (ctx, dest) => _pick([
        `${dest} esta en la lista de TODOS ultimamente.

${ctx.inquiryCount || 'Varias'} familias me preguntaron por este destino en las ultimas 2 semanas. Y tiene sentido: los parques estan con eventos especiales, los precios todavia no suben tanto, y cada vez mas familias se animan.

Si tu tambien lo tienes en mente, hablemos antes de que suban los precios.

${_cta()}

${_hashtags(dest)}`,

        `"Sara, quiero ir a ${dest}."

Lo vengo escuchando mucho ultimamente. ${ctx.inquiryCount || 'Varias'} familias en las ultimas 2 semanas. Cuando un destino se pone de moda, los precios empiezan a subir y la disponibilidad de hoteles baja.

Mi recomendacion: si lo estan pensando, no dejen pasar mucho tiempo. La planificacion temprana siempre ahorra plata.

${_cta()}

${_hashtags(dest)}`,
    ]),

    reel: (ctx, dest) => _pick([
        `GUION REEL (15-30 seg)

Hook: "Todo el mundo me esta preguntando por ${dest}"

Cuerpo:
- "${ctx.inquiryCount || 'Varias'} consultas en 2 semanas"
- Mostrar por que es tan popular ahora
- Un tip que la gente no sabe

Cierre: "Te cuento mas? Escribeme"

${_reelAudio()}`,

        `GUION REEL (15-30 seg)

Hook: "Por que todos quieren ir a ${dest} ahora?"

Cuerpo:
- "Llevo ${ctx.inquiryCount || 'varias'} consultas en 2 semanas"
- "La razon: [evento especial / temporada / precios]"
- "Pero ojo: los precios van subiendo"

Cierre: "Si esta en tus planes, hablemos ya. Link en bio."

${_reelAudio()}`,
    ]),

    story: (ctx, dest) => `SECUENCIA DE STORIES (3 stories)

Story 1:
Texto: "${dest} es tendencia"
Detalle: "${ctx.inquiryCount || 'Varias'} familias preguntando"

Story 2:
Encuesta: "${dest} esta en tus planes?"
Opciones: "Si, quiero ir!" / "Cuentame mas"

Story 3:
Texto: "Hablemos antes de que suban los precios"
Sticker: Link / DM`,

    carousel: (ctx, dest) => `CARRUSEL (5 slides para Canva)

Slide 1:
"Por que ${dest} esta de moda ahora?"

Slide 2:
"${ctx.inquiryCount || 'Varias'} familias lo estan consultando"
Cuando un destino se pone de moda, los precios suben y la disponibilidad baja.

Slide 3:
"Eventos especiales"
Los parques tienen temporadas y eventos que hacen que ciertos meses sean mejores.

Slide 4:
"Reservar temprano ahorra plata"
La diferencia entre reservar 6 meses antes vs 2 meses puede ser del 20-30%.

Slide 5 (CTA):
"No dejes pasar la oportunidad"
"Cotizacion gratis. Link en bio."`,

    whatsapp: (ctx, dest) => `${dest} esta de moda! Llevo ${ctx.inquiryCount || 'varias'} consultas en 2 semanas. Si ustedes tambien lo tienen en mente, es buen momento para cotizar antes de que suban precios. Me escriben!`,
}

// ========== COUNTDOWN TEMPLATES ==========

const COUNTDOWN = {
    caption: (ctx, dest) => {
        const days = ctx.travelDate ? Math.ceil((new Date(ctx.travelDate) - new Date()) / (1000*60*60*24)) : '?'
        return _pick([
            `Faltan ${days} dias para que otra familia viva la magia de ${dest}.

Ya tienen todo listo: reservas, parques, dining plan. Solo falta hacer las maletas.

Este es el tipo de tranquilidad que da planificar con tiempo. Sin sorpresas, sin estres, sin "ay, se nos olvido".

${_cta()}

${_hashtags(dest)}`,

            `${days} dias. Eso es lo que falta para que esta familia llegue a ${dest}.

Hotel confirmado. Parques listos. Dining reservations hechas. Lightning Lane programado. Itinerario entregado.

Cuando todo esta planificado, el unico trabajo que queda es disfrutar.

${_cta()}

${_hashtags(dest)}`,
        ])
    },

    reel: (ctx, dest) => {
        const days = ctx.travelDate ? Math.ceil((new Date(ctx.travelDate) - new Date()) / (1000*60*60*24)) : '?'
        return `GUION REEL (15-30 seg)

Hook: "${days} dias para ${dest}"

Cuerpo:
- Checklist visual: Hotel (check), Parques (check), Dining (check), Lightning Lane (check)
- "Todo confirmado, todo listo"
- "Asi se ve un viaje bien planificado"

Cierre: "Quieres que tu viaje se vea asi? Link en bio"

${_reelAudio()}`
    },

    story: (ctx, dest) => {
        const days = ctx.travelDate ? Math.ceil((new Date(ctx.travelDate) - new Date()) / (1000*60*60*24)) : '?'
        return `SECUENCIA DE STORIES (3 stories)

Story 1:
Sticker: Countdown ${days} dias
Texto: "Otra familia lista para ${dest}"

Story 2:
Texto: "Todo planificado. Solo falta la maleta."
Sticker: Checklist animado

Story 3:
Encuesta: "Ustedes ya tienen todo listo para su viaje?"
Opciones: "Si!" / "Todavia no"`
    },

    carousel: (ctx, dest) => {
        const days = ctx.travelDate ? Math.ceil((new Date(ctx.travelDate) - new Date()) / (1000*60*60*24)) : '?'
        return `CARRUSEL (5 slides para Canva)

Slide 1:
"${days} dias para la magia"
${dest}

Slide 2: "Hotel: Confirmado"
El hotel no es solo donde dormir. Es donde empieza la experiencia.

Slide 3: "Parques: Estrategia lista"
Que parque cada dia, donde empezar, que filas evitar.

Slide 4: "Restaurantes: Reservados"
Dining plan, reservaciones, recomendaciones de comida.

Slide 5 (CTA):
"Tu viaje puede estar asi de listo"
"Empecemos a planificar. Link en bio."`
    },

    whatsapp: (ctx, dest) => {
        const days = ctx.travelDate ? Math.ceil((new Date(ctx.travelDate) - new Date()) / (1000*60*60*24)) : '?'
        return `Faltan ${days} dias para que otra familia viaje a ${dest}. Todo planificado: hotel, parques, restaurantes. Si quieren vivir esa tranquilidad en su proximo viaje, me escriben!`
    },
}

// ========== REPEAT CLIENT TEMPLATES ==========

const REPEAT = {
    caption: (ctx) => _pick([
        `Cuando un cliente vuelve a reservar, sabes que hiciste algo bien.

${ctx.tripCount || 2} viajes juntos y contando. Eso no se logra con una cotizacion bonita. Se logra con servicio real, atencion al detalle, y estar ahi antes, durante y despues del viaje.

Gracias por la confianza.

#ClientesFelices #Fidelidad #ViajesEnFamilia #Mind2Magic`,

        `Viaje numero ${ctx.tripCount || 2} con esta familia.

La primera vez llegaron con mil dudas. "Sera que si vale la pena?" "No sera muy caro?" "Sera que los ninos se van a divertir?"

Hoy ya ni preguntan. Me dicen la fecha y yo les armo todo. Eso se llama confianza.

Gracias a cada familia que vuelve. Ustedes son la mejor publicidad.

#ClientesFelices #Mind2Magic`,
    ]),

    reel: (ctx) => _pick([
        `GUION REEL (15-30 seg)

Hook: "Este cliente ya va por su viaje #${ctx.tripCount || 2} conmigo"

Cuerpo:
- "La primera vez fue [destino 1]"
- "Ahora van a [destino 2]"
- "La diferencia? Ya saben que todo va a estar perfecto"

Cierre: "Quieres esa tranquilidad? Hablemos"

${_reelAudio()}`,

        `GUION REEL (15-30 seg)

Hook: "La mejor publicidad no se paga"

Cuerpo:
- "Es cuando un cliente vuelve por su viaje #${ctx.tripCount || 2}"
- "Sin preguntar precios primero"
- "Sin comparar con otras agencias"
- "Solo dice: 'Sara, armame otro'"

Cierre: "Eso se gana con servicio, no con descuentos"

${_reelAudio()}`,
    ]),

    story: (ctx) => `SECUENCIA DE STORIES (3 stories)

Story 1:
Texto: "Viaje #${ctx.tripCount || 2} con esta familia"
Fondo: Foto de destino

Story 2:
Texto: "Cuando un cliente vuelve, sabes que hiciste bien tu trabajo"
Emoji slider: "Nivel de satisfaccion"

Story 3:
Texto: "Ustedes tambien pueden viajar con esa confianza"
Sticker: Link en bio`,

    carousel: (ctx) => `CARRUSEL (4 slides para Canva)

Slide 1:
"Viaje #${ctx.tripCount || 2} con esta familia"

Slide 2:
"La primera vez: mil preguntas, muchas dudas"
Es normal. Un viaje a Disney o un crucero es una inversion grande.

Slide 3:
"Hoy: me dicen la fecha y yo hago el resto"
La confianza se construye viaje a viaje.

Slide 4 (CTA):
"Tu primera vez puede ser asi de facil"
"Escribeme y te cuento como funciona"`,

    whatsapp: (ctx) => `Acabo de confirmar el viaje #${ctx.tripCount || 2} con una misma familia. Cuando un cliente vuelve, es la mejor senal de que uno esta haciendo bien las cosas. Si quieren planificar su viaje con alguien de confianza, me escriben!`,
}

// ========== SEASONAL TEMPLATES ==========

const SEASONAL = {
    caption: (ctx) => _pick([
        `${ctx.theme || 'Temporada especial'}.

${ctx.tip || 'Es buen momento para planificar tu viaje.'}

Muchas familias no saben que hay temporadas donde los precios son mas bajos, los parques estan menos llenos, y la experiencia es igual de magica. La clave es planificar con anticipacion.

${_cta()}

#ViajesEnFamilia #TravelPlanner #Mind2Magic`,

        `Si estan pensando en viajar, presten atencion a esto:

${ctx.theme || 'Hay oportunidades que no pueden dejar pasar.'}

${ctx.tip || 'La planificacion temprana ahorra plata y estres.'}

Las familias que planifican con meses de anticipacion siempre consiguen mejores precios, mejores hoteles, y mejores restaurantes.

${_cta()}

#ViajesEnFamilia #Tips #Mind2Magic`,
    ]),

    reel: (ctx) => `GUION REEL (15-30 seg)

Hook: "${ctx.theme || 'Algo que deben saber si van a viajar'}"

Cuerpo:
- ${ctx.tip || 'Tip de temporada'}
- Por que es importante actuar ahora
- Un consejo practico que pueden aplicar hoy

Cierre: "Quieres que te ayude a planificar? Link en bio"

${_reelAudio()}`,

    story: (ctx) => `SECUENCIA DE STORIES (3 stories)

Story 1:
Texto: "${ctx.theme || 'Temporada especial'}"
Fondo: Foto tematica

Story 2:
Texto: "${ctx.tip || 'Tip de temporada'}"
Sticker: Informativo

Story 3:
Caja de preguntas: "Tienen dudas sobre esta temporada?"
Seguimiento: Responder en stories`,

    carousel: (ctx) => `CARRUSEL (4 slides para Canva)

Slide 1:
"${ctx.theme || 'Lo que necesitan saber'}"

Slide 2:
"Por que importa"
${ctx.tip || 'La temporada afecta precios, disponibilidad y experiencia.'}

Slide 3:
"Que hacer ahora"
La planificacion temprana siempre gana. Mejor hotel, mejor precio, mejor experiencia.

Slide 4 (CTA):
"Les ayudo a planificar"
"Cotizacion gratis. Escribeme."`,

    whatsapp: (ctx) => `Hola! Les comparto un dato importante: ${ctx.theme || 'hay oportunidades de temporada'}. ${ctx.tip || 'Es buen momento para planificar.'} Si quieren cotizar, me escriben!`,
}

// ========== MILESTONE TEMPLATES ==========

const MILESTONE = {
    caption: (ctx) => _pick([
        `${ctx.milestone || 'Muchos'} viajes. ${ctx.milestone || 'Muchas'} familias. Mucha magia.

Cada viaje que planifico es unico. Cada familia tiene su historia, sus suenos, y sus miedos. Mi trabajo es convertir esos suenos en un plan real, sin sorpresas.

Gracias a cada familia que confio en mi. Este numero no es solo mio, es de todos.

#Agradecimiento #ViajesEnFamilia #Mind2Magic`,

        `Hoy celebro: ya son ${ctx.milestone || 'muchos'}+ viajes planificados.

Detras de cada numero hay una familia que decidio confiar. Que decidio dejar de improvisar y planificar de verdad.

No fue facil llegar hasta aca, pero cada "Sara, fue el mejor viaje de nuestras vidas" hace que valga la pena.

Gracias. Vamos por mas.

#Milestone #TravelPlanner #Mind2Magic`,
    ]),

    reel: (ctx) => `GUION REEL (15-30 seg)

Hook: "Ya son ${ctx.milestone || 'muchos'}+ viajes planificados"

Cuerpo:
- Montaje rapido de fotos/screenshots de confirmaciones
- "Cada uno con su historia"
- "Gracias a cada familia que confio"

Cierre: "Tu puedes ser el siguiente. Link en bio."

${_reelAudio()}`,

    story: (ctx) => `SECUENCIA DE STORIES (3 stories)

Story 1:
Texto: "${ctx.milestone || 'Muchos'}+ viajes"
Sticker: Celebracion

Story 2:
Texto: "Gracias a cada familia que confio"
Fondo: Collage de destinos

Story 3:
Encuesta: "El proximo viaje puede ser el tuyo"
Opciones: "Vamos!" / "Algun dia"`,

    carousel: (ctx) => `CARRUSEL (4 slides para Canva)

Slide 1:
"${ctx.milestone || 'Muchos'}+ viajes planificados"
Un numero que representa confianza.

Slide 2:
"Cada viaje es una historia"
Disney, Universal, cruceros, hoteles... cada familia con su sueno.

Slide 3:
"Lo que mas importa"
No es el numero. Es saber que cada familia llego tranquila y disfruto al maximo.

Slide 4 (CTA):
"El proximo puede ser el tuyo"
"Hablemos. Link en bio."`,

    whatsapp: (ctx) => `Ya son ${ctx.milestone || 'muchos'}+ viajes planificados. Gracias a cada familia que ha confiado. Si ustedes tambien quieren planificar su viaje, estoy aqui para ayudarles.`,
}

// ========== DEMAND SIGNAL TEMPLATES ==========

const DEMAND = {
    caption: (ctx, dest) => {
        const label = ctx.typeLabel || dest
        return _pick([
            `Esta semana, ${ctx.count || 'varias'} familias me preguntaron por ${label}.

Cuando veo que un tipo de viaje se repite tanto, es porque algo esta pasando. Puede ser un evento especial, una promocion, o simplemente que mas familias se estan animando.

Si tu tambien lo tienes en mente, hablemos. Es mejor planificar temprano que quedarse sin disponibilidad.

${_cta()}

${_hashtags(dest)}`,

            `"Sara, quiero un ${label.toLowerCase()}."

Lo vengo escuchando mucho. ${ctx.count || 'Varias'} veces esta semana.

Cada caso es diferente: fechas, presupuesto, edades, preferencias. Pero todos tienen algo en comun: quieren vivir esa experiencia en familia.

Si tu tambien estas pensando en un ${label.toLowerCase()}, empecemos a planificar.

${_cta()}

${_hashtags(dest)}`,
        ])
    },

    reel: (ctx, dest) => {
        const label = ctx.typeLabel || dest
        return _pick([
            `GUION REEL (15-30 seg)

Hook: "Las ${ctx.count || 3} preguntas que TODOS me hacen sobre ${label}"

Cuerpo:
1. Cuanto cuesta? Depende de muchos factores.
2. Cuando es la mejor epoca? Hay temporadas mejores que otras.
3. Vale la pena? Absolutamente.

Cierre: "Tienes otra pregunta? Dejala en comentarios"

${_reelAudio()}`,

            `GUION REEL (15-30 seg)

Hook: "Esta semana todos me preguntan por ${label}"

Cuerpo:
- "${ctx.count || 'Varias'} consultas en una semana"
- "Por que esta de moda?"
- Un dato que la gente no sabe

Cierre: "Quieres saber mas? Escribeme"

${_reelAudio()}`,
        ])
    },

    story: (ctx, dest) => {
        const label = ctx.typeLabel || dest
        return `SECUENCIA DE STORIES (3 stories)

Story 1:
Texto: "${label}: ${ctx.count || 'varias'} consultas esta semana"
Fondo: Foto tematica

Story 2:
Caja de preguntas: "Que quieres saber sobre ${label.toLowerCase()}?"

Story 3:
Texto: "Les respondo todas las dudas manana"
Seguimiento: Responder en stories`
    },

    carousel: (ctx, dest) => {
        const label = ctx.typeLabel || dest
        return `CARRUSEL (5 slides para Canva)

Slide 1:
"Todo sobre ${label}"
Lo que necesitas saber antes de reservar

Slide 2:
"Cuanto cuesta?"
Depende de la temporada, el hotel, y cuantos viajan. Pero hay opciones para todos los presupuestos.

Slide 3:
"Mejor epoca para ir"
Cada mes tiene ventajas y desventajas. Lo importante es planificar con tiempo.

Slide 4:
"Que incluye la planificacion"
Cotizacion, hotel, parques, restaurantes, itinerario dia por dia.

Slide 5 (CTA):
"Hablemos de TU viaje"
"Cotizacion gratis. Link en bio."`
    },

    whatsapp: (ctx, dest) => {
        const label = ctx.typeLabel || dest
        return `${label} esta en tendencia! ${ctx.count || 'Varias'} familias preguntando esta semana. Si tambien les interesa, me escriben y les armo su cotizacion gratis.`
    },
}

// ========== FAQ TEMPLATES ==========

const FAQ = {
    caption: (ctx) => _pick([
        `Esta semana, ${ctx.count || 'varias'} familias me preguntaron sobre ${ctx.topic || 'este tema'} a traves del formulario.

Las dudas mas comunes siempre son las mismas: cuanto cuesta, cuando ir, que hotel elegir. Y la respuesta siempre es: depende de tu familia.

No hay un viaje "estandar". Cada familia tiene sus tiempos, su presupuesto, y sus prioridades. Por eso siempre empiezo con una conversacion, no con un precio.

${_cta()}

${_hashtags(ctx.topic || '')}`,

        `"Pero Sara, es muy complicado planificar un viaje a ${ctx.topic || 'estos destinos'}?"

No. Lo que es complicado es improvisar. Cuando tienes a alguien que conoce cada detalle, todo fluye.

${ctx.count || 'Varias'} familias me consultaron por ${ctx.topic || 'este tema'} esta semana. Todas con la misma duda. Y la respuesta es mas sencilla de lo que piensan.

${_cta()}

${_hashtags(ctx.topic || '')}`,
    ]),

    reel: (ctx) => `GUION REEL (15-30 seg)

Hook: "La pregunta que TODOS me hacen sobre ${ctx.topic || 'viajes'}"

Cuerpo:
- Mostrar la pregunta en pantalla
- Responder con datos reales (no genericos)
- Un tip extra que nadie pregunta pero todos necesitan

Cierre: "Tienes otra pregunta? Dejala en comentarios o llena el formulario"

${_reelAudio()}`,

    story: (ctx) => `SECUENCIA DE STORIES (3 stories)

Story 1:
Texto: "${ctx.topic || 'Pregunta frecuente'}"
Detalle: "${ctx.count || 'Varias'} familias preguntaron"

Story 2:
Texto: La respuesta corta (1-2 lineas)

Story 3:
Caja de preguntas: "Que dudas tienen sobre ${ctx.topic || 'su viaje'}?"
Seguimiento: Responder al dia siguiente`,

    carousel: (ctx) => `CARRUSEL (5 slides para Canva)

Slide 1:
"Lo que todos preguntan sobre ${ctx.topic || 'viajar'}"

Slide 2:
Pregunta 1: "Cuanto cuesta?"
Respuesta corta y honesta.

Slide 3:
Pregunta 2: "Cuando es mejor ir?"
La respuesta cambia segun la familia.

Slide 4:
Pregunta 3: "Vale la pena?"
Absolutamente. Y aca te explico por que.

Slide 5 (CTA):
"Tienes mas preguntas?"
"Escribeme. La asesoria es gratis."`,

    whatsapp: (ctx) => `Varias familias me han preguntado sobre ${ctx.topic || 'este tema'}. Si ustedes tambien tienen dudas, me pueden escribir. Les ayudo con mucho gusto, sin compromiso.`,
}

// ========== TYPE MAP ==========

const TYPE_MAP = {
    booking: BOOKING,
    quote: QUOTE,
    trending: TRENDING,
    countdown: COUNTDOWN,
    repeat: REPEAT,
    seasonal: SEASONAL,
    milestone: MILESTONE,
    demand: DEMAND,
    faq: FAQ,
}

// ========== MAIN GENERATOR ==========

export function generateEnhancedDraft(idea, formatId) {
    const ctx = idea.dataContext || {}
    const dest = ctx.destination || ctx.typeLabel || 'un destino increible'
    const travelers = ctx.travelers || ''

    const typeTemplates = TYPE_MAP[idea.type]
    if (!typeTemplates) {
        return `${idea.title}\n\n${idea.subtitle || ''}\n\nDesarrolla este contenido segun el angulo elegido.\n\n#ViajesEnFamilia #Mind2Magic`
    }

    const generator = typeTemplates[formatId]
    if (!generator) {
        return `${idea.title}\n\n${idea.subtitle || ''}\n\nDesarrolla este contenido para formato ${formatId}.\n\n#ViajesEnFamilia #Mind2Magic`
    }

    return generator(ctx, dest, travelers)
}
